import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import { correctlyFormatDate, today } from 'utils/dates';
import { LOYALTY_COMPANION_PRODUCT_CODES } from 'constants/booking';
import { MAX_NUMBER_OF_COMPANION_VOUCHER } from 'constants/clubEurostar';
import { INTERACTIVE_TOOLTIP_HIDE_DELAY_MS } from 'constants/tooltip';
import { GET_BOOKINGS_BY_PRODUCT_CODE } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { getBookingJourneysURL } from 'utils/urls';

import { RemainingContainer, TooltipContainer, RemainingLabel } from './MemberInfo.styles';

const CompanionVouchers = ({ customerEmail, customerName, pointsResetDate }) => {
  const { location } = useHistory();

  const startDate = correctlyFormatDate(moment(pointsResetDate).subtract(1, 'year'));
  const endDate = moment(today()).isBefore(moment(pointsResetDate)) ? today() : pointsResetDate;

  const { data, loading, error } = useQuery(GET_BOOKINGS_BY_PRODUCT_CODE, {
    variables: {
      startDate,
      endDate,
      passengerName: customerName,
      passengerEmail: customerEmail,
      productCodes: LOYALTY_COMPANION_PRODUCT_CODES,
      customerBookingsOnly: true
    },
    fetchPolicy: 'network-only',
    skip: !customerEmail || !customerName
  });

  const { getBookingsByProductCode = [] } = data ?? {};

  const handleReferenceClick = (reference) => () =>
    window.open(getBookingJourneysURL(reference, location.pathname.split('/')?.[2] ?? null));

  return (
    <RemainingContainer data-cy="companion-vouchers-remaining">
      <FormattedMessage id="clubEurostar.companionVouchersRemaining" tagName="span" />
      {loading ? (
        <Spinner type={'circular'} />
      ) : error ? (
        '-'
      ) : (
        <>
          <RemainingLabel data-for="bookings-with-companion-vouchers" data-tip="" data-cy="companion-vouchers-tooltip-label">
            {Math.max(MAX_NUMBER_OF_COMPANION_VOUCHER - getBookingsByProductCode.length, 0)}
          </RemainingLabel>
          <ReactTooltip
            className="tooltip"
            id="bookings-with-companion-vouchers"
            data-cy="bookings-with-companion-vouchers"
            place="bottom"
            effect="solid"
            delayHide={INTERACTIVE_TOOLTIP_HIDE_DELAY_MS}
          >
            <TooltipContainer>
              <FormattedMessage id="clubEurostar.companionVouchersConfirmedBookings" />
              {getBookingsByProductCode.length === 0 ? (
                <div>
                  <FormattedMessage id="clubEurostar.companionVouchersNoBookingsFound" />
                </div>
              ) : (
                <ul>
                  {getBookingsByProductCode.map(({ reference }) => (
                    <li key={reference} data-cy={`companion-vouchers-booking-link-${reference}`} onClick={handleReferenceClick(reference)}>
                      {reference}
                    </li>
                  ))}
                </ul>
              )}
            </TooltipContainer>
          </ReactTooltip>
        </>
      )}
    </RemainingContainer>
  );
};

CompanionVouchers.propTypes = {
  customerEmail: PropTypes.string,
  customerName: PropTypes.string,
  pointsResetDate: PropTypes.string
};

export default CompanionVouchers;
